import React, {memo} from "react";
import classNames from "classnames";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {DateTime} from "luxon";
import {LavoratorePresenzeRecord} from "@commons/mapping/LavoratorePresenzeRecord";
import BasicBadge from "@components/BasicBadge";
import TooltipEx from "@components/Tooltip";
import {BadgeTurno, BadgeTurnoByDay} from "./BadgeTurno";
import {TurnoRecord} from "@commons/mapping/TurnoRecord";
import {GiustificativoRecord} from "@commons/mapping/GiustificativoRecord";
import {ComboButton} from "@components/ComboButton";
import {PageType} from "../../dashboardWF/wf.dashboard.detail";
import {IconArrowBackUp, IconClock} from "@tabler/icons-react";
import TagIconFilled from "../../../../icons/TagIconFilled";
import {BadgePresenzeSTD} from "../../comps/DayCellDisclosure";
import {mm2HHmmFormat} from "@commons/utils/util";
import {RiepilogoOrePlus} from "@commons/mapping/RiepilogoOrePlus";

export const hasAnomalia = function (anomalia: number) {
    return anomalia !== 0;
};

export const hasWarning = function (warning: number, warningChecked: number) {
    if (warning === 0) {
        return false;
    } else {
        return (warning ^ warningChecked) !== 0;
    }
}


interface BannerDayProps {
    name?: string,
    lavoratore: LavoratorePresenzeRecord,
    day: PresenzeGiornataPlusRecord,
    turni: TurnoRecord[],
    giustificativi: GiustificativoRecord[],
    modal?: 'giustificativi' | 'orario' | 'unitaLocali',
    onResetOrario?: (day: PresenzeGiornataPlusRecord) => void,
    page: PageType,
    descrizioneUL?: string,

    anomalia: number,
}

export const BannerDay: React.FC<BannerDayProps> = ({
                                                        name, lavoratore, day, turni, giustificativi, modal,
                                                        onResetOrario, page, descrizioneUL, ...props
                                                    }) => {

    const getBgColorGiornata = (page: PageType, anomalia: number, warning: number, warningChecked: number) => {
        if (page === 'Presenze') {
            if (hasAnomalia(anomalia)) {
                switch (anomalia) {
                    case -3:
                        return 'bg-daCalcolare';
                    default:
                        return 'bg-bgAnomalia text-black';
                }
            }
            if (hasWarning(warning, warningChecked)) {
                return 'bg-warning';
            }
            if (!hasAnomalia(anomalia) && !hasWarning(warning, warningChecked)) {
                return 'bg-ggQuadrata';
            }
        } else return 'bg-gray-100'
    }


    const giornoDt = DateTime.fromISO(day.dtGiornata!)
    const bgColor = (page !== 'Presenze' && props.anomalia > 0) ? 'bg-anomalia text-white' : getBgColorGiornata(page, props.anomalia, day.warning, day.warningChecked)
    const pagePianificazione = page === 'Pianificazione';

    const hhPrev = mm2HHmmFormat(RiepilogoOrePlus.getTotaleHPrev(day.totali));
    const hhLav = mm2HHmmFormat(RiepilogoOrePlus.getTotaleHLav(day.totali));

    return <div className={classNames(bgColor, 'px-4 pb-1', page === 'Presenze' ? 'text-sm' : '')}>
        <div className="py-2 text-center text-red-700">{name}</div>
        <div className="container mx-auto grid grid-cols-2 sm:grid-cols-5 gap-4">
            <div className="row-span-2">
                Id contratto
                <br/>
                #{lavoratore.id}
            </div>
            <div className="row-span-2">
                Nominativo
                <br/>
                <span className="font-bold text-[15px] capitalize">
                    {lavoratore.descrizione}
                </span>
            </div>
            <div className="row-span-2">
                Giorno di riferimento
                <br/>
                {/*<span className="font-bold text-[15px]">{giornoDt.toFormat('dd/LL/yyyy')}</span>*/}
                <span className="font-bold text-[15px] capitalize">
                    {DateTime.local(giornoDt.year, giornoDt.month, giornoDt.day).weekdayLong} {giornoDt.toLocaleString(DateTime.DATE_FULL)}</span>
            </div>
            {(page === 'Presenze' && descrizioneUL) && <div className="row-span-2">
                UL
                <br/>
                c/o {descrizioneUL}
            </div>}
            <div className="row-span-2">
                Previsto{pagePianificazione && '/Pianificato'}
                <br/>
                <div className="inline-flex space-x-1 text-xs text-center">
                    <BadgeTurno elemento={turni.find(t => t.codTurno === day.codTurno)} idGiorno={day.idGiorno}></BadgeTurno>&nbsp;
                    {page === 'Presenze' && <span className="my-auto font-bold text-[13px] inline-flex">
                        <IconClock className="size-4"/><TooltipEx tooltip="Ore lavorate">{hhLav}</TooltipEx>
                        &nbsp;/&nbsp;
                        <TooltipEx tooltip="Ore previste">{hhPrev}</TooltipEx>
                    </span>}
                    {pagePianificazione && <BadgeTurnoByDay {...{day, turni, giustificativi}}/>}
                </div>
            </div>
            {pagePianificazione && <div className="row-span-2">
                {(modal === 'orario' && day.codTurnoPianificazione > 0) ?
                    <ComboButton Icon={IconArrowBackUp} onClick={() => {
                        if (onResetOrario) onResetOrario(day);
                    }}>
                        Turno sviluppato
                    </ComboButton> : <></>}
            </div>}
        </div>
        <div className="flex flex-row pt-2 pb-1 justify-between">
            <div className="flex flex-row w-fit">
                {LavoratorePresenzeRecord.getStatus(lavoratore, day.dtGiornata)?.map((status, index) => {
                    const tooltip = <div>Status {status.descrizione} [#{status.codice}]
                        <ul className="list-disc list-inside">
                            <li>dal {DateTime.fromISO(status.decorrenza!).toFormat('dd/LL/yyyy')}</li>
                            {status.fineValidita && <li>al {DateTime.fromISO(status.fineValidita!).toFormat('dd/LL/yyyy')}</li>}
                        </ul>
                    </div>
                    return <div key={index} className="self-center mx-1 first:ml-0 last:mr-0">
                        <TooltipEx className="z-50 p-2 rounded bg-gray-700 text-white text-sm w-max"
                                   tooltip={tooltip}>
                            <div>
                                <BasicBadge
                                    label={<><TagIconFilled className={`mr-1 ${page === 'Presenze' ? 'h-4 w-4' : 'h-5 w-5'}`} aria-hidden="true"/>{status.descrizione}</>}
                                    type={"primary"}
                                    containerClass={page === 'Presenze' ? 'text-xs' : 'text-sm'}/>
                            </div>
                        </TooltipEx>
                    </div>
                })}
            </div>

            {/* SE LA GIORNATA è GESTITA IN MODALITA' STANDARD */}
            {day.tipoRilevazione === 'MANUALE' && <BadgePresenzeSTD className="!text-sm"/>}
        </div>
    </div>;
}

export const MemoizedBannerDay = memo(BannerDay);
export default BannerDay;